// @ts-strict-ignore
import React, { useContext } from 'react';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import type {
  Control, UseFormRegister, FieldErrors, UseFormSetValue,
} from 'react-hook-form';
import {
  Controller,
} from 'react-hook-form';

import {
  DatePicker,
  Text,
  TextField,
  Box,
} from '@usealloy/component-library';

import type {
  DataType,
} from './InputAttributeConfigurationTab/types';

import {
  StyledDatePickerWrapper,
  StyledErrorText,
} from './styles';

import { UserContext } from '~/dashboard/context';
import {
  getDefaultValueDateError,
  getDefaultValueNumberError,
  getDefaultValueStringError,
} from '~/dashboard/views/settings/published-attributes/components/utils';

export type DefaultValueFieldProps = {
  control: Control<any>;
  register: UseFormRegister<any>;
  dataType: DataType;
  errors: FieldErrors;
  setValue: UseFormSetValue<any>;
  disabled?: boolean;
};

const DefaultValueField = ({
  dataType,
  control,
  register,
  errors,
  setValue,
  disabled = false,
}: DefaultValueFieldProps) => {
  const { featureFlags } = useContext(UserContext);

  switch (dataType) {
    case 'date':
    case 'timestamp':
      return (
        <Box justifyContent="flex-start" direction="column" alignItems="flex-start">
          <Text style={{ marginBottom: 'var(--cl-space-1)' }}>
            <span style={{ color: 'var(--cl-background-danger)' }}>*</span>
            Default Value
          </Text>
          <Controller
            control={control}
            name="defaultValue"
            rules={{
              required: 'Please enter a default value.',
              validate: (value, { validValueMin, validValueMax }) => {
                if (featureFlags.paValidationImprovements) {
                  return (
                    getDefaultValueDateError(value, validValueMin, validValueMax)
                  );
                }
                return null;
              },
              deps: featureFlags?.paValidationImprovements
                ? ['validValueMax', 'validValueMin']
                : [],
            }}
            render={({
              field: {
                value, onChange, ref: fieldRef, ...field
              },
            }) => {
              const dateFormat = dataType === 'date'
                ? 'YYYY-MM-DD'
                : 'YYYY-MM-DDTHH:mm:ss[Z]';

              return (
                <StyledDatePickerWrapper>
                  <DatePicker
                    {...field}
                    ref={fieldRef}
                    value={value ? dayjs(value, dateFormat) : null}
                    showTime={dataType === 'timestamp'}
                    onChange={(date: Dayjs) => {
                      onChange(date.format(dateFormat));
                    }}
                    style={{ width: '100%' }}
                    status={errors.defaultValue ? 'error' : ''}
                    disabled={disabled}
                  />
                </StyledDatePickerWrapper>
              );
            }}
          />
          {errors.defaultValue?.message && (
            <Box justifyContent="flex-start" style={{ marginTop: 'var(--cl-space-1)' }}>
              <StyledErrorText>
                {errors.defaultValue?.message as unknown as React.ReactNode}
              </StyledErrorText>
            </Box>
          )}
        </Box>
      );
    case 'integer':
    case 'decimal':
      return (
        <TextField
          type="number"
          step={dataType === 'integer' ? 1 : 0.1}
          label="Default Value"
          error={errors.defaultValue?.message as unknown as React.ReactNode}
          {...register('defaultValue', {
            required: 'Please enter a default value.',
            onBlur: ({ target: { value } }) => {
              if (value && dataType === 'integer') {
                setValue('defaultValue', parseInt(value, 10).toString(), { shouldDirty: true });
              }
            },
            validate: (value, { validValueMin, validValueMax }) => {
              if (featureFlags?.paValidationImprovements) {
                return (
                  getDefaultValueNumberError(value, validValueMin, validValueMax, dataType)
                );
              }
              return null;
            },
            deps: featureFlags?.paValidationImprovements
              ? ['validValueMax', 'validValueMin']
              : [],
          })}
          required
          disabled={disabled}
        />
      );
    default:
      return (
        <TextField
          type="text"
          label="Default Value"
          placeholder={dataType === 'string' ? '(empty string)' : ''}
          error={errors.defaultValue?.message as unknown as React.ReactNode}
          {...register('defaultValue', {
            required: false,
            onBlur: () => { },
            validate: (value, { validValues }) => {
              if (featureFlags.paValidationImprovements) {
                return (
                  getDefaultValueStringError(value, validValues)
                );
              }
              return null;
            },
            deps: featureFlags?.paValidationImprovements
              ? ['validValues']
              : [],
          })}
          disabled={disabled}
        />
      );
  }
};

export default DefaultValueField;
